import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import DCheckbox from '../Global/DCheckbox';
import FiveStarReviewIcon from '../Global/FiveStarReviewIcon';
import { applyDiscountOffer, getDowngradeInsights, saveDowngradeReason, saveDowgradeBookMeeting, getCheckoutSession, saveDowngradeStep, finishCancelDowngrade, submitFeedback, sendDowngradeEmail, saveChangedTier } from '@/services/plans.service';
import useToast from '@/hooks/useToast';
import DLoading from '../DLoading';
import { useUserStore } from '@/stores/user/userStore';
import CalendarIcon from '../Global/Icons/CalendarIcon';
import DSpinner from '../Global/DSpinner';

// Define the reasons array outside the component to avoid re-creation on each render
const DOWNGRADE_REASONS = [
  {
    id: 'repetitive',
    label: '<b>Eliminate repetitive questions</b> - Stop answering the same questions daily'
  },
  {
    id: 'coverage',
    label: '<b>True 24/7 coverage</b> - Handle inquiries while you sleep or vacation'
  },
  {
    id: 'response',
    label: '<b>Instant response times</b> - Go from hours/days to under 30 seconds'
  },
  {
    id: 'growth',
    label: '<b>Handle growth without hiring</b> - Scale to 10x volume with same team'
  },
  {
    id: 'brand',
    label: '<b>Consistent brand voice</b> - Every answer sounds professional and on-brand'
  },
  {
    id: 'freedom',
    label: '<b>Free yourself completely</b> - Never be the bottleneck for customer questions'
  },
  {
    id: 'workload',
    label: '<b>Cut support workload in half</b> - Reduce tickets/emails by 50%+'
  },
  {
    id: 'complex',
    label: '<b>Handle complex inquiries</b> - Multi-step processes, not just simple FAQs'
  },
  {
    id: 'other',
    label: 'Other'
  }
];

const NewDowngradeModal = ({ isOpen, onClose: propOnClose, nextTier }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedReasons, setSelectedReasons] = useState([]);
  const [otherReason, setOtherReason] = useState('');
  const [downgradeInsights, setDowngradeInsights] = useState(null);
  const { addSuccessToast } = useToast();
  const [firstAnswer, setFirstAnswer] = useState('');
  const [secondAnswer, setSecondAnswer] = useState('');
  const [thirdAnswer, setThirdAnswer] = useState('');
  const user = useUserStore(state => state.user);
  
  // Loading states for buttons
  const [isAcceptOfferLoading, setIsAcceptOfferLoading] = useState(false);
  const [isBookMeetingLoading, setIsBookMeetingLoading] = useState(false);
  const [isSwitchPlanLoading, setIsSwitchPlanLoading] = useState(false);
  const [isCancelDowngradeLoading, setIsCancelDowngradeLoading] = useState(false);
  const [isSubmitFeedbackLoading, setIsSubmitFeedbackLoading] = useState(false);

  const handleClose = async () => {
    try {
      if (downgradeInsights?.tier_downgrade_reason?.id) {
        // Save the current step before closing
        await saveDowngradeStep({
          tier_downgrade_reason_id: downgradeInsights.tier_downgrade_reason.id,
          step: currentStep
        });
        
        // Send downgrade email if closing on step 6
        if (currentStep === 6) {
          try {
            await sendDowngradeEmail({
              tier_downgrade_reason_id: downgradeInsights.tier_downgrade_reason.id
            });
          } catch (error) {
            console.error('Error sending downgrade email:', error);
          }
        }
      }
    } catch (error) {
      console.error('Error saving downgrade step:', error);
    } finally {
      // Reset current step
      setCurrentStep(1);
      setSelectedReasons([]);
      setOtherReason('');
      setFirstAnswer('');
      setSecondAnswer('');
      setThirdAnswer('');
      setDowngradeInsights(null);
      propOnClose();
    }
  };


  // Helper function to check if an object is empty
  const isEmptyObject = (obj) => {
    return obj && Object.keys(obj).length === 0 && obj.constructor === Object;
  };

  const handleNextStep = async () => {
    // If we're on step 2 and moving to step 3, ensure the reasons are saved
    if (currentStep === 2 && downgradeInsights?.tier_downgrade_reason?.id) {
      try {
        // Map the selected reason IDs to their full text values using the DOWNGRADE_REASONS array
        const reasonsText = selectedReasons
          .map(id => {
            // Find the reason object with the matching ID
            const reasonObj = DOWNGRADE_REASONS.find(reason => reason.id === id);
            // Extract the text content from the HTML label
            if (reasonObj) {
              // Remove HTML tags from the label
              return reasonObj.label.replace(/<\/?[^>]+(>|$)/g, "");
            }
            return id;
          })
          .join(', ');
        
        // Add the other reason if provided
        const fullReasonText = reasonsText + 
          (selectedReasons.includes('other') && otherReason ? `: ${otherReason}` : '');
          
        const reasonData = {
          tier_downgrade_reason_id: downgradeInsights.tier_downgrade_reason.id,
          reason: fullReasonText,
        };
        
        await saveDowngradeReason(reasonData);
      } catch (error) {
        console.error('Error saving downgrade reason during next step:', error);
      }
    }
    
    // Skip step 4 if downgradeInsights.tier is an empty object
    if (currentStep === 3 && isEmptyObject(downgradeInsights?.tier)) {
      setCurrentStep(5); // Skip to step 5
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 1) {
      // Skip step 4 when going backward if downgradeInsights.tier is an empty object
      if (currentStep === 5 && isEmptyObject(downgradeInsights?.tier)) {
        setCurrentStep(3); // Go back to step 3
      } else {
        setCurrentStep(currentStep - 1);
      }
    }
  };

  const handleAcceptOffer = async () => {
    try {
      setIsAcceptOfferLoading(true);
      const response = await applyDiscountOffer(user?.id);
      if(response?.status === 200){
        addSuccessToast({message: '50% discount applied to your next payment.'});
        handleClose();
      }
    } catch(error) {
      console.log(error);
      addSuccessToast({message: error?.response?.data?.detail || 'Failed to apply discount. Please try again.', type: 'error'});
    } finally {
      setIsAcceptOfferLoading(false);
    }
  };
   
  const handleBookMeeting = async () => {
    try {
      setIsBookMeetingLoading(true);
      if (downgradeInsights?.tier_downgrade_reason?.id) {
        const data = {
          tier_downgrade_reason_id: downgradeInsights.tier_downgrade_reason.id
        };
        
        const response = await saveDowgradeBookMeeting(data);
        if (response?.status === 200) {
          window.open('https://calendly.com/jetelira/dante-ai-demo', '_blank');
          handleClose();
        }
      }
    } catch (error) {
      console.error('Error booking meeting:', error);
      addSuccessToast({message: 'Failed to book meeting. Please try again.', type: 'error'});
    } finally {
      setIsBookMeetingLoading(false);
    }
  };

  const handleSwitchPlan = async () => {
    try {
      setIsSwitchPlanLoading(true);
      if (downgradeInsights?.tier?.id) {
        const response = await getCheckoutSession(downgradeInsights.tier.id);
        
        if (response?.status === 200 && response?.data?.checkout_session?.url) {
          // Open checkout URL in a new tab
          window.open(response.data.checkout_session.url, '_blank');
          handleClose(); // Close the modal after opening the checkout URL
        }
      }
    } catch (error) {
      console.error('Error switching plan:', error);
      addSuccessToast({message: error?.response?.data?.detail || error?.response?.data?.message || 'Failed to process your request. Please try again.', type: 'error'});
    } finally {
      setIsSwitchPlanLoading(false);
    }
  };
  
  const handleCancelDowngrade = async () => {
    try {
      setIsCancelDowngradeLoading(true);
      const response = await finishCancelDowngrade();
      
      if (response?.status === 200) {
        addSuccessToast({message: 'Your subscription has been canceled.'});
        setCurrentStep(6); // Move to feedback step after cancellation
      }
    } catch (error) {
      console.error('Error canceling subscription:', error);
      addSuccessToast({message: error?.response?.data?.detail || error?.response?.data?.message || 'Failed to cancel your subscription. Please try again.', type: 'error'});
    } finally {
      setIsCancelDowngradeLoading(false);
    }
  };


  const handleReasonChange = (reason) => {
    if (selectedReasons.includes(reason)) {
      setSelectedReasons(selectedReasons.filter(item => item !== reason));
    } else {
      setSelectedReasons([...selectedReasons, reason]);
    }
  };

  const handleSubmitFeedback = async () => {
    try {
      setIsSubmitFeedbackLoading(true);
      const response = await submitFeedback({
        first_answer: firstAnswer, 
        second_answer: secondAnswer, 
        third_answer: thirdAnswer, 
        tier_downgrade_reason_id: downgradeInsights?.tier_downgrade_reason?.id
      });
      if(response?.status === 200){
        addSuccessToast({message: 'Feedback submitted successfully.'});        
        handleClose();
      }
    } catch (error) {
      console.log(error);
      addSuccessToast({message: error?.response?.data?.detail || error?.response?.data?.message || 'Failed to submit feedback. Please try again.', type: 'error'});
    } finally {
      setIsSubmitFeedbackLoading(false);
    }
  }
  const getDowngradeInsightsData = async () => {
    try{
        const response = await getDowngradeInsights();
        if(response?.status === 200){
            setDowngradeInsights(response?.data);
            
            if(response?.data?.tier_downgrade_reason?.id && nextTier) {
                try {
                    await saveChangedTier({
                        tier_downgrade_reason_id: response.data.tier_downgrade_reason.id,
                        previous_tier: user?.tier_type,
                        new_tier: nextTier.name.toLowerCase()
                    });
                } catch (error) {
                    console.error('Error saving changed tier:', error);
                }
            }
        }
    }catch(error){
        console.log(error);
    }
  }

  useEffect(() => {
    if(isOpen){
      getDowngradeInsightsData();
    }
  }, [isOpen]);

  if(!downgradeInsights){
    return <DLoading />;
  }

  const renderStep = () => {
    switch (currentStep) {
        case 1:
            return (
            <div className="flex flex-col items-center w-full md:w-[632px]">
                {/* Background blurred circles */}
            
                <div className="w-[544px] h-[544px] rounded-full opacity-15 bg-[#10B981] blur-[75px] absolute -top-[77px] -left-[92px] "></div>
                <div className="w-[469px] h-[469px] rounded-full opacity-10 bg-[#10B981] blur-[75px] absolute -bottom-[118px] -right-[186px] "></div>

                {/* Content */}
                <div className="flex flex-col items-center w-full z-10 relative">
                <h1 className="text-[24px] font-bold font-['DM_Sans'] text-black-max leading-normal tracking-[-0.72px] mb-8 text-wrap">
                    Let's see how Dante AI is performing for you
                </h1>

                {/* Cards container */}
                <div className="flex flex-col items-start w-full gap-2 mb-8">
                    {/* First row of cards */}
                    <div className="flex items-center gap-2 w-full flex-col md:flex-row">
                    {/* Card 1 - Resolved Customer Queries */}
                    <div className="flex flex-col items-start py-6 pb-8 gap-4 flex-1 rounded-size3 bg-white/30 min-w-[200px]">
                        <div className="flex flex-col items-center w-full gap-size3">
                        <p className="text-[14px] font-medium font-['DM_Sans'] text-black-max leading-[16px] text-center">
                            {downgradeInsights?.stats?.resolved_customer_queries?.label || "Resolved Customer Queries"}
                        </p>
                        <p className="text-[32px] md:text-[48px] font-semibold font-['DM_Sans'] text-[#059669] leading-[100%] tracking-[-1px] text-center">
                            {downgradeInsights?.stats?.resolved_customer_queries?.value || 0}
                        </p>
                        </div>
                    </div>
                    
                    {/* Card 2 - Hours of your time saved */}
                    <div className="flex flex-col items-start py-6 pb-8 gap-4 flex-1 rounded-size3 bg-white/30 min-w-[200px]">
                        <div className="flex flex-col items-center w-full gap-size3">
                        <p className="text-[14px] font-medium font-['DM_Sans'] text-black-max leading-[16px] text-center">
                            {downgradeInsights?.stats?.hours_of_your_time_saved?.label || "Hours of Your Time Saved"}
                        </p>
                        <p className="text-[32px] md:text-[48px] font-semibold font-['DM_Sans'] text-[#059669] leading-[100%] tracking-[-1px] text-center">
                            {downgradeInsights?.stats?.hours_of_your_time_saved?.value || 0}
                        </p>
                        </div>
                    </div>
                    </div>

                    {/* Second row of cards - conditionally render based on ROI value */}
                    <div className={`flex items-center gap-2 w-full flex-col md:flex-row ${downgradeInsights?.stats?.roi_achieved?.value === null ? 'justify-center' : ''}`}>
                    {/* Card 3 - Cost Recovered */}
                    <div className="flex flex-col items-start py-6 pb-8 gap-4 flex-1 rounded-size3 bg-white/30 min-w-[200px]">
                        <div className="flex flex-col items-center w-full gap-size3">
                        <p className="text-[14px] font-medium font-['DM_Sans'] text-black-max leading-[16px] text-center">
                            {downgradeInsights?.stats?.cost_recovered?.label || "Cost Recovered"}
                        </p>
                        <p className="text-[32px] md:text-[48px] font-semibold font-['DM_Sans'] text-[#059669] leading-[100%] tracking-[-1px] text-center">
                            {downgradeInsights?.stats?.cost_recovered?.value || "$0"}
                        </p>
                        </div>
                    </div>
                    
                    {/* Card 4 - ROI Achieved - Only show if value is not null */}
                    {downgradeInsights?.stats?.roi_achieved?.value !== null && (
                        <div className="flex flex-col items-start py-6 pb-8 gap-4 flex-1 rounded-size3 bg-white/30 min-w-[200px]">
                            <div className="flex flex-col items-center w-full gap-size3">
                            <p className="text-[14px] font-medium font-['DM_Sans'] text-black-max leading-[16px] text-center">
                                {downgradeInsights?.stats?.roi_achieved?.label || "ROI Achieved"}
                            </p>
                            <p className="text-[32px] md:text-[48px] font-semibold font-['DM_Sans'] text-[#059669] leading-[100%] tracking-[-1px] text-center">
                                {downgradeInsights?.stats?.roi_achieved?.value}
                            </p>
                            </div>
                        </div>
                    )}
                    </div>
                </div>

                {/* Buttons */}
                <div className="flex flex-col justify-center items-center gap-4 w-full mt-2 md:mt-16">
                    <button 
                      className="flex h-[40px] w-full px-6 py-[10px] justify-center items-center flex-1 bg-accent text-white text-[14px] font-medium font-['Inter'] leading-[160%] rounded-[4px] hover:bg-accent-hover"
                      onClick={handleNextStep}
                    >
                      I want better results
                    </button>
                    <button 
                      className="flex h-[40px] w-full px-6 py-[10px] justify-center items-center flex-1 text-accent text-[14px] font-medium font-['Inter'] leading-[160%] rounded-[4px] hover:bg-accent/5"
                      onClick={() => setCurrentStep(4)}
                    >
                      I want to explore other options
                    </button>
               
                </div>
                </div>
            </div>
            );
        case 2:
            return (
            <div className="flex flex-col items-center w-full md:w-[632px]">
                <h1 className="text-[24px] font-bold font-['DM_Sans'] text-black-max leading-normal tracking-[-0.72px] mb-8 text-left w-full">
                What's your biggest priority right now?
                </h1>

                <div className="flex flex-col items-start gap-[10px] mb-8 w-full rounded-[4px]">
                    {DOWNGRADE_REASONS.map((reason) => (
                    <div key={reason.id} className="flex flex-col w-full">
                      <div className="flex items-center gap-4">
                        <DCheckbox
                            checked={selectedReasons.includes(reason.id)}
                            onChange={() => handleReasonChange(reason.id)}
                            // className="w-5 h-5 accent-accent"
                            label={reason.label}
                            checkboxSize='flex-shrink-0 !w-5 !h-5'
                        />
                      </div>
                      {reason.id === 'other' && selectedReasons.includes('other') && (
                        <div className="mt-2 ml-6 w-full animate-fadeIn">
                          <textarea
                            className="w-full border border-[rgba(9,8,31,0.15)] rounded-[8px] bg-white p-[9px_13px] min-h-[80px] flex justify-center items-center flex-1 text-sm font-['Inter']"
                            placeholder="Please specify your priority..."
                            value={otherReason}
                            onChange={(e) => setOtherReason(e.target.value)}
                          />
                        </div>
                      )}
                    </div>
                ))}
                </div>

                <div className="flex justify-center w-full">
                <button 
                    className="flex h-[40px] px-6 py-[10px] justify-center items-center bg-accent text-white text-[14px] font-medium font-['Inter'] leading-[160%] rounded-[4px] hover:bg-accent-hover w-full"
                    onClick={handleNextStep}
                    disabled={selectedReasons.length === 0 || (selectedReasons.includes('other') && !otherReason.trim())}
                    
                >
                    Continue
                </button>
                </div>
            </div>
            );
        case 3: 
            return (
                <div className="flex flex-col items-center w-full md:w-[632px] gap-0 md:gap-8">
                    <div className='w-full'>
                        <h1 className="text-[24px] font-bold font-['DM_Sans'] text-black-max leading-normal tracking-[-0.72px] mb-8 text-left w-full">
                        Let’s solve this in 15 mins optimization session
                        </h1>

                        <div className='flex flex-col items-start w-full gap-4 mb-8'>
                            <div className="flex items-center gap-3">
                                <span className="text-[#10B981] text-xl">✅</span>
                                <p className="text-[16px] font-medium text-black-max" dangerouslySetInnerHTML={{ __html: '<b>We audit your current setup </b> against top-performing configurations' }} />
                            </div>
                            <div className="flex items-center gap-3">
                                <span className="text-[#10B981] text-xl">✅</span>
                                <p className="text-[16px] font-medium text-black-max" dangerouslySetInnerHTML={{ __html: '<b> We identify gaps </b> that are limiting your results' }} />
                            </div>
                            <div className="flex items-center gap-3">
                                <span className="text-[#10B981] text-xl">✅</span>
                                <p className="text-[16px] font-medium text-black-max" dangerouslySetInnerHTML={{ __html: '<b> We help you make improvements live </b> to close those gaps immediately' }} />
                            </div>
                            <div className="flex items-center gap-3">
                                <span className="text-[#10B981] text-xl">✅</span>
                                <p className="text-[16px] font-medium text-black-max" dangerouslySetInnerHTML={{ __html: '<b> We set success tracking </b>  so you can measure the difference' }} />
                            </div>
                            <div className="flex items-center gap-3">
                                <span className="text-[#10B981] text-xl">✅</span>
                                <p className="text-[16px] font-medium text-black-max" dangerouslySetInnerHTML={{ __html: '<b> We build your roadmap </b>  to reach top-performer status' }} />
                            </div>
                        </div>
                    </div>
             
                    <div className='w-full'> 
                        <h1 className="text-[24px] font-bold font-['DM_Sans'] text-black-max leading-normal tracking-[-0.72px] mb-8 text-left w-full">
                            Hear from our customers who took the shot
                        </h1>
                        <div className="flex gap-size5 items-stretch">
                            <div className="flex flex-col flex-1 grow min-h-[180px]">
                                <div className="flex flex-col items-start">
                                    <FiveStarReviewIcon />
                                    <p className="text-sm italic leading-[150%] mt-size3">
                                        It's so reliable and efficient that it now takes care of 95% of our customer service.
                                    </p>
                                </div>
                                <div className="flex-1 flex flex-col justify-end">
                                    <p className="text-sm italic leading-[150%] text-right mt-size3">
                                        – Trustpilot user, USA
                                    </p>
                                </div>
                            </div>
                            <div className="flex flex-col flex-1 grow min-h-[180px]">
                                <div className="flex flex-col items-start">
                                    <FiveStarReviewIcon />
                                    <p className="text-sm italic leading-[150%] mt-size3">
                                        The Dante team is incredibly supportive, and their tools, like the analytics and live chat options, show they value customer satisfaction.
                                    </p>
                                </div>
                                <div className="flex-1 flex flex-col justify-end">
                                    <p className="text-sm italic leading-[150%] text-right mt-size3">
                                        – Trustpilot user, USA
                                    </p>
                                </div>
                            </div>
                            <div className="flex flex-col flex-1 grow min-h-[180px]">
                                <div className="flex flex-col items-start">
                                    <FiveStarReviewIcon />
                                    <p className="text-sm italic leading-[150%] mt-size3">
                                        Dante AI increased our sales significantly due to higher customer satisfaction rates, and a smoother purchase journey for our customers.
                                    </p>
                                </div>
                                <div className="flex-1 flex flex-col justify-end">
                                    <p className="text-sm italic leading-[150%] text-right mt-size3">
                                        – Trustpilot user, USA
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className='flex flex-col w-full gap-size3 mt-8'>
                        <button 
                          className='py-[10px] px-size5 bg-[#5745FA] text-white rounded-size1 font-medium flex items-center gap-size1 justify-center'
                          onClick={handleBookMeeting}
                          disabled={isBookMeetingLoading}
                        >
                          <CalendarIcon />
                          {isBookMeetingLoading ? 'Processing...' : 'Book my FREE optimization session'}
                        </button>
                        <button className='py-[10px] px-size5 text-[#5745FA] rounded-size1 font-medium' onClick={handleNextStep}>
                            I'd rather explore other options
                        </button>

                    </div>
                </div>
            );
        case 4:
            return (
                <div className="flex flex-col items-center w-full md:w-[632px] gap-8">
                    <h1 className="text-[24px] font-bold font-['DM_Sans'] text-black-max leading-normal tracking-[-0.72px] text-left w-full mb-2">
                        We want to find the right fit for you
                    </h1>
                    <div className='flex flex-col items-start w-full gap-1'>
                        <p className='tracking-tight text-[20px] font-bold'>Scale Down</p>
                        <p className='text-base'>Keep the wins, reduce the commitment</p>
                    </div>

                    <div className='flex flex-col gap-size3 w-full'>
                        <p>✅ <b>Preserve:</b> Your 1,247 trained conversations</p>
                        <p>✅ <b>Maintain:</b> Professional automated responses</p>
                        <p>✅ <b>Keep:</b> All integrations and setup work</p>
                        <p>✅ <b>Flexibility:</b> Upgrade instantly when you're ready</p>
                    </div>

                    <div className='flex flex-col gap-size3 w-full mt-8'>
                        <button 
                            className='py-[10px] px-size5 bg-[#5745FA] text-white rounded-size1 font-medium' 
                            onClick={handleSwitchPlan}
                            disabled={isSwitchPlanLoading}
                        >
                            {isSwitchPlanLoading ? 'Processing...' : `Switch to ${downgradeInsights?.tier?.name || "Starter Plan"} ($${downgradeInsights?.tier?.price || 29}/mo)`}
                        </button>
                        <button className='py-[10px] px-size5 text-[#5745FA] rounded-size1 font-medium' onClick={handleNextStep}>
                        I still want to cancel
                        </button>
                    </div>
                </div>
            );
        case 5:
            return (
                <div className="flex flex-col items-start w-full md:w-[632px] gap-8">
                    <div>
                        <h1 className="text-[24px] font-bold font-['DM_Sans'] text-black-max leading-normal tracking-[-0.72px] text-left w-full mb-2">
                            One last way we can help
                        </h1>
                        <p className='text-base'>You know your business best. Sometimes timing just isn't right.</p>
                    </div>

                    <div className='py-12 bg-[#F3F1FF] rounded-2xl flex flex-col items-center justify-center gap-size3 w-full'>
                        <p className='text-[40px] font-[700] -tracking-wide text-[#362B9D]'>🎁 50% off for 2 months</p>
                        <p className='text-base'>Reduce your cost while you decide</p>
                    </div>

                    <div className='flex flex-col gap-size3 w-full mt-8'>
                        <button 
                            className='py-[10px] px-size5 bg-[#5745FA] text-white rounded-size1 font-medium flex items-center gap-size1 justify-center' 
                            onClick={handleAcceptOffer}
                            disabled={isAcceptOfferLoading}
                        >
                            {isAcceptOfferLoading ? <>Accepting offer<DSpinner color='#FFFFFF' style={{ height: 25, width: 25 }} /> </> : 'Accept the exclusive OFFER'}
                        </button>
                        <button 
                            className='py-[10px] px-size5 text-[#5745FA] rounded-size1 font-medium flex items-center gap-size1 justify-center' 
                            onClick={handleCancelDowngrade}
                            disabled={isCancelDowngradeLoading}
                        >
                            {isCancelDowngradeLoading ? <>Processing<DSpinner color='#5745FA' style={{ height: 25, width: 25 }} /> </> : 'Proceed with cancellation'}
                        </button>
                    </div>
                </div>
            );
        case 6:
            return (
                <div className="flex flex-col w-full md:w-[632px] gap-8">
                    <div className='flex flex-col gap-size0'>
                        <h1 className="text-[24px] font-bold font-['DM_Sans'] text-black-max leading-normal tracking-[-0.72px] text-left w-full">
                        Thank you for trusting us!
                        </h1>
                        <p className='text-base'>Before you go, help us improve for the future</p>
                    </div>
                    <div className='flex flex-col gap-size5 w-full mt-8'>
                        <div className='flex flex-col gap-size1'>
                            <p className='text-base'>What initially made you excited about Dante AI?</p>
                            <textarea
                                className='w-full border border-[rgba(9,8,31,0.15)] rounded-[8px] bg-white p-[9px_13px] min-h-[80px] flex justify-center items-center flex-1'
                                // placeholder='Type your answer here...'
                                value={firstAnswer}
                                onChange={(e) => setFirstAnswer(e.target.value)}
                            />
                        </div>
                        <div className='flex flex-col gap-size1'>
                            <p className='text-base'>What worked well during your time with us?</p>
                            <textarea
                                className='w-full p-2 border border-gray-300 rounded-[4px] min-h-[80px]'
                                // placeholder='Type your answer here...'
                                value={secondAnswer}
                                onChange={(e) => setSecondAnswer(e.target.value)}
                            />
                        </div>
                        <div className='flex flex-col gap-size1'>
                            <p className='text-base'>What would make automated customer service perfect for your business?</p>
                            <textarea
                                className='w-full border border-[rgba(9,8,31,0.15)] rounded-[8px] bg-white p-[9px_13px] min-h-[80px] flex justify-center items-center flex-1'
                                // placeholder='Type your answer here...'
                                value={thirdAnswer}
                                onChange={(e) => setThirdAnswer(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className='flex flex-col'>
                        <button 
                            className='py-[10px] px-size5 bg-[#5745FA] text-white rounded-size1 font-medium mt-4' 
                            onClick={handleSubmitFeedback}
                            disabled={isSubmitFeedbackLoading}
                        >
                            {isSubmitFeedbackLoading ? 'Submitting...' : 'Submit feedback'}
                        </button>
                        <button className='py-[10px] px-size5 text-[#5745FA] rounded-size1 font-medium' onClick={handleClose}>    
                            Skip
                        </button>
                    </div>
                </div>
            );
      default:
        return null;
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      className="relative z-50 transition-all"
    >
      <DialogBackdrop
        className="fixed inset-0 duration-150 bg-black/30 data-[closed]:opacity-0 data-[leave]:delay-100"
      />

      <div className="fixed inset-0 flex w-screen items-center justify-center transition-all">
        <DialogPanel
          className={`transition-all duration-200 w-[90%] md:w-[760px] min-h-[590px] bg-white rounded-size3 flex flex-col items-center justify-center p-8  data-[enter]:delay-100 data-[closed]:-translate-y-20 data-[closed]:opacity-0 relative overflow-hidden`}
        >
          {renderStep()}
        </DialogPanel>
      </div>
    </Dialog>
  );
};

NewDowngradeModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};

export default NewDowngradeModal;
